import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { usePricing } from "@/lib/pricing";

const FLAG_PREFIX = "deckscore_grants_ext_upsell_shown:";

async function shouldShow(): Promise<boolean> {
  const { data } = await supabase.auth.getUser();
  const uid = data.user?.id;
  if (!uid) return false;
  try {
    return !localStorage.getItem(FLAG_PREFIX + uid);
  } catch {
    return false;
  }
}

async function markShown() {
  const { data } = await supabase.auth.getUser();
  const uid = data.user?.id;
  if (!uid) return;
  try {
    localStorage.setItem(FLAG_PREFIX + uid, "1");
  } catch {
    /* ignore */
  }
}

/**
 * Global open helper - set by the component's effect.
 * Callers: `window.__deckscoreExtUpsell?.trigger()`
 */
declare global {
  interface Window {
    __deckscoreExtUpsell?: { trigger: () => Promise<void>; isOpen: () => boolean };
  }
}

export function ExtensionUpsellModal() {
  const [open, setOpen] = useState(false);
  const pricing = usePricing();

  useEffect(() => {
    window.__deckscoreExtUpsell = {
      trigger: async () => {
        if (await shouldShow()) {
          await markShown();
          setOpen(true);
        }
      },
      isOpen: () => open,
    };
    return () => {
      if (window.__deckscoreExtUpsell) delete window.__deckscoreExtUpsell;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-ink/60 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-[460px] rounded-2xl border border-line bg-surface p-7 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="font-script text-accent text-[22px] leading-none">stop copy-pasting</p>
        <h2 className="mt-2 font-display text-[24px] font-semibold text-ink">
          Autofill this application.
        </h2>
        <p className="mt-3 text-[14.5px] text-ink-secondary leading-relaxed">
          The <strong className="text-ink">DeckScore Grants Autofill</strong> Chrome extension writes
          answers from your pitch deck analysis - problem, traction, market - not just your name and
          email. <strong className="text-ink">3 forms free a month</strong>, then {pricing.proMonthlyDisplay} for 25 forms a month.
        </p>
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-full border border-line bg-surface px-4 py-2.5 text-[13.5px] font-medium text-ink-secondary hover:text-ink"
          >
            Maybe later
          </button>
          <Link
            to="/grants/extension"
            onClick={() => setOpen(false)}
            className="inline-flex items-center justify-center rounded-full bg-accent px-5 py-2.5 text-[13.5px] font-bold text-accent-ink hover:bg-accent-press"
          >
            Get the extension →
          </Link>
        </div>
      </div>
    </div>
  );
}
